// TODO update this to import from commons
// import { gql } from '@reverbdotcom/commons/src/gql';
// eslint-disable-next-line no-restricted-imports
import { gql } from '@apollo/client';
import { useEffect, useState } from 'react';
import { last, compact, clone } from 'lodash';

import experiments from '@reverbdotcom/commons/src/experiments';
import { trackEvent, MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import { formatExperimentsForEventAttributes, EXPERIMENT_DELIMITER } from '@reverbdotcom/commons/src/elog/mparticle';
import { mParticleListingFields } from '@reverbdotcom/commons/src/components/with_mparticle_listings';
import { trackCriteoProductView } from '@reverbdotcom/commons/src/criteo';

import { Item2ProductViewTrackingData, reverb_signals_Signal_Name } from '@reverbdotcom/commons/src/gql/graphql';
import { IProps } from './item2_layout';
import { convertForExperimentBucketing, Experimentable } from '@reverbdotcom/commons/src/non_user_experiments';
import { getImages } from './item2_image';

import { IUser } from '@reverbdotcom/commons/src/components/user_context_provider';
import { currencies } from '@reverbdotcom/commons/src/constants';
import { eligibleForOutlet } from '@reverbdotcom/commons/src/user_context_helpers';
import { isEU } from '@reverbdotcom/commons/src/geo';

type Listing = Item2ProductViewTrackingData.Fragment;

function isUSA(countryCode) {
  return [
    'US',
    'US_CON',
    'US_AK',
    'US_HI',
  ].includes(countryCode);
}

export function isDomesticToBuyer(userShippingRegionCode, sellerCountryCode) {
  if (!(userShippingRegionCode && sellerCountryCode)) return null;
  return userShippingRegionCode === sellerCountryCode || (isUSA(userShippingRegionCode) && isUSA(sellerCountryCode));
}

function getBrandCategory(brandSlug, categoryUuids) {
  const leaf = last(categoryUuids);
  if (brandSlug && leaf) {
    return `${brandSlug}:${leaf}`;
  }
}

function formatEligibleListingExperimentsForEventAttributes(listing: Listing): string {
  const trackedListingExperiments = [];
  const experimentableListing = convertForExperimentBucketing(listing as Experimentable);
  return formatExperimentsForEventAttributes(experimentableListing, trackedListingExperiments);
}

function addGreatValueBadgeEUExperiment(trackedExperiments: string[], user: Partial<IUser>, listing) {
  const isGreatValue = listing.signals?.find(signal => signal.name === reverb_signals_Signal_Name.GREAT_VALUE);

  if (isGreatValue && isEU(user.shippingRegionCode) && listing.currency === 'EUR') {
    trackedExperiments.push(experiments.GREAT_VALUE_BADGE_EU);
  }
}

function addGreatValueBadgeUKExperiment(trackedExperiments: string[], user: Partial<IUser>, listing) {
  const isGreatValue = listing.signals?.find(signal => signal.name === reverb_signals_Signal_Name.GREAT_VALUE);

  if (isGreatValue && user.shippingRegionCode === 'GB' && listing.currency === 'GBP') {
    trackedExperiments.push(experiments.NEW_GREAT_VALUE_UK_MODEL);
  }
}

function addAffirmCADExperiment(trackedExperiments: string[], user: Partial<IUser>, listing: Listing) {
  if (listing.acceptedPaymentMethods?.includes('affirm') && user.currency === currencies.CAD && listing.currency === currencies.CAD) {
    trackedExperiments.push(experiments.AFFIRM_CAD);
  }
}

function addItemPageOutletBrowseRowExperiment(trackedExperiments: string[], user: Partial<IUser>) {
  if (eligibleForOutlet(user)) {
    trackedExperiments.push(experiments.ITEM_PAGE_OUTLET_BROWSE_ROW);
  }
}

export const ALWAYS_TRACKED_EXPERIMENTS = [
  experiments.DUMMY,
  experiments.MV_DUMMY,
  experiments.ACCEPTED_PAYMENT_METHODS,
  experiments.ORANGE_SECONDARY_CTAS_ON_ITEM_PAGE,
  experiments.WEB_TYPICAL_NEW_PRICE,
];

export default function useProductViewAction(props: IProps) {
  const [hasViewed, setHasViewed] = useState(false);

  useEffect(() => {
    if (hasViewed) {
      return;
    }

    const { data, user } = props;
    const trackedExperiments = clone(ALWAYS_TRACKED_EXPERIMENTS);

    if (data.listing) {
      addGreatValueBadgeEUExperiment(trackedExperiments, user, data.listing);
      addGreatValueBadgeUKExperiment(trackedExperiments, user, data.listing);
      addAffirmCADExperiment(trackedExperiments, user, data.listing);
      addItemPageOutletBrowseRowExperiment(trackedExperiments, user);
      const brandCategory = getBrandCategory(data.listing.brandSlug, data.listing.categoryUuids);

      const customTrackedValues = {
        /**
         * if an attribute needs to be tracked upon listing view but isn't product data, add it to this data array instead of "listing"
         *
         * examples of product data: listing ID, listing type
         * examples of NOT product data: whether the item is available nearby the buyer, the item's restocking fee
         *
         * If in doubt, ask a data scientist for clarification
         */
        domesticToBuyer: isDomesticToBuyer(user.shippingRegionCode, data.listing.shop?.address?.countryCode),
        signals: data.listing.signals?.map(signal => signal.name).join('|'),
        shipsToBuyer: Boolean(data.listing.shipping?.shippingPrices[0]),
        viewedPriceDrop: !!data.listing.pricing?.ribbon,
        imageCount: getImages(data.listing, 'cardSquareImages').length,
        reviewCount: data.listing.csp?.productReviewSearch?.stats?.total,
        enabledForAffirmZeroPercentFinancing: !!data.listing.affirmFinancingPromotion?.zeroPercentFinancing,
        certified: !!data.listing.shop?.certifiedPreOwned,
        ...(brandCategory ? { brandCategory } : {}),
      };

      const formattedListingExperiments = formatEligibleListingExperimentsForEventAttributes(data.listing);
      const formattedUserExperiments = formatExperimentsForEventAttributes(user, trackedExperiments);
      const formattedUserAndListingExperiments = compact([
        formattedUserExperiments,
        formattedListingExperiments,
      ]).join(EXPERIMENT_DELIMITER);

      trackEvent({
        eventName: MParticleEventName.ProductViewAction,
        listing: data.listing,
        experiments: formattedUserAndListingExperiments,
        ...customTrackedValues,
      });

      trackCriteoProductView(data.listing.id, user);

      setHasViewed(true);
    }
  }, [props, hasViewed]);
}

export const Item2ProductViewTrackingFragment = gql`
  fragment Item2ProductViewTrackingData on Listing {
    _id
    acceptedPaymentMethods
    ...mParticleListingFields
  }
  ${mParticleListingFields}
`;
